import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import axiosInstance from '../services/axiosInstance';
import { getAuthToken, getMe, logout as authLogout } from '../services/authService';
import { hasAnyPermission, hasPermission } from '../utils/permissionUtils';
import { IAsset, ICategory, ITemplate, IUser } from '../types/damTypes';
import { useAuthSessionLifecycle } from '../hooks/useAuthSessionLifecycle';

import LoginPage from '../pages/LoginPage';
import SettingsPage from '../pages/SettingsPage';
import AssetDetails from '../components/AssetDetails';
import TemplateManagementModal from '../components/TemplateManagementModal';

import BrowseScreen from './screens/BrowseScreen';
import UploadScreen from './screens/UploadScreen';
import CartScreen from './screens/CartScreen';
import MoreScreen from './screens/MoreScreen';
import MobileFooterNav, { MobileTab } from './components/MobileFooterNav';
import InhouseModule from '../modules/inhouse';
import ListningarModule from '../modules/listningar';
import { getDeepLinkedAssetId } from '../utils/assetDeepLink';
import MaintenanceNotice from '../modules/maintenance/MaintenanceNotice';

type Toast = { open: boolean; message: string; severity: 'success' | 'info' | 'warning' | 'error' };

export default function AppMobile() {
  const [tab, setTab] = React.useState<MobileTab>('browse');

  const [currentUser, setCurrentUser] = React.useState<IUser | null>(null);
  const [authLoading, setAuthLoading] = React.useState(true);

  const [categories, setCategories] = React.useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = React.useState(false);
  const [categoryError, setCategoryError] = React.useState<string | null>(null);

  const [templates, setTemplates] = React.useState<ITemplate[]>([]);

  const [assets, setAssets] = React.useState<IAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = React.useState(false);
  const [assetError, setAssetError] = React.useState<string | null>(null);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<string[]>([]);

  // Keep mobile search aligned with desktop defaults.
  const desktopDefaultSearchFields = React.useMemo(
    () => ['title', 'description', 'tags', 'aiTags', 'aiDescription', 'fileName', 'metadata'],
    []
  );

  // Default: 2 per row. Persist locally.
  const [columns, setColumns] = React.useState<1 | 2>(() => {
    try {
      const raw = localStorage.getItem('mobileGridColumns');
      return raw === '1' ? 1 : 2;
    } catch {
      return 2;
    }
  });
  React.useEffect(() => {
    try { localStorage.setItem('mobileGridColumns', String(columns)); } catch {}
  }, [columns]);

  // NOTE (not active): We may force 1 column on very small phones later.
  // TODO(mobile): If needed, add an override for very small widths (< ~340px).

  const [cartCount, setCartCount] = React.useState(0);

  const [selectedAssetId, setSelectedAssetId] = React.useState<string | null>(null);
  const detailSwipeStartYRef = React.useRef<number | null>(null);
  const deepLinkedAssetIdRef = React.useRef<string | null>(getDeepLinkedAssetId());
  const deepLinkHandledRef = React.useRef(false);

  const [settingsView, setSettingsView] = React.useState<any>(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [templatesOpen, setTemplatesOpen] = React.useState(false);

  const [toast, setToast] = React.useState<Toast>({ open: false, message: '', severity: 'info' });

  const showToast = (message: string, severity: Toast['severity'] = 'info') => {
    setToast({ open: true, message, severity });
  };

  const refreshCartCount = React.useCallback(async () => {
    try {
      const resp = await axiosInstance.get('/api/cart');
      const items = Array.isArray(resp.data) ? resp.data : [];
      const total = items.reduce((sum: number, it: any) => sum + (Number(it?.quantity) || 0), 0);
      setCartCount(total);
    } catch {
      // Quiet failure; cart screen will show errors if needed.
    }
  }, []);

  const fetchCategories = React.useCallback(async () => {
    try {
      setLoadingCategories(true);
      setCategoryError(null);
      const response = await axiosInstance.get<ICategory[]>(`/api/categories`);
      setCategories(response.data);
    } catch (err: any) {
      setCategoryError(err.response?.data?.message || err.message || 'Error fetching categories');
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchTemplates = React.useCallback(async () => {
    try {
      const response = await axiosInstance.get<ITemplate[]>(`/api/templates`);
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch {
      // Keep quiet; AssetDetails can still load basic asset info without templates.
      setTemplates([]);
    }
  }, []);

  const fetchAssets = React.useCallback(async () => {
    try {
      setLoadingAssets(true);
      setAssetError(null);

      const params: Record<string, any> = { archived: 'false' };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
        // Desktop behavior: AND default + explicit search fields list
        params.operator = 'AND';
        params.searchFields = desktopDefaultSearchFields.join(',');
      }
      if (selectedCategoryIds.length > 0) params.category = selectedCategoryIds.join(',');

      const response = await axiosInstance.get<IAsset[]>(`/api/assets`, { params });
      setAssets(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setAssetError(err.response?.data?.message || err.message || 'Error fetching assets');
    } finally {
      setLoadingAssets(false);
    }
  }, [searchTerm, selectedCategoryIds, desktopDefaultSearchFields]);

  const addToCart = React.useCallback(async (assetId: string) => {
    try {
      const response = await axiosInstance.post(`/api/cart/add`, { assetId, quantity: 1 });
      if (response.status === 200) {
        showToast('Tillagd i varukorg', 'success');
        refreshCartCount();
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Kunde inte lägga till artikel i varukorgen';
      showToast(msg, 'error');
    }
  }, [refreshCartCount]);

  const resetAfterLogout = React.useCallback(() => {
    setCurrentUser(null);
    setAssets([]);
    setCategories([]);
    setCartCount(0);
    setSelectedAssetId(null);
    setSettingsOpen(false);
    setTemplatesOpen(false);
    setTab('browse');
  }, []);

  const handleLogout = async (options?: { reason?: string; server?: boolean }) => {
    await authLogout(options);
    resetAfterLogout();
  };

  useAuthSessionLifecycle(currentUser, () => {
    resetAfterLogout();
  });

  // Bootstrap auth on mount
  React.useEffect(() => {
    const boot = async () => {
      setAuthLoading(true);
      try {
        const token = getAuthToken();
        if (!token) {
          setCurrentUser(null);
          return;
        }
        const me = await getMe();
        if (me.data?.user) {
          setCurrentUser(me.data.user);
        } else {
          await authLogout();
          setCurrentUser(null);
        }
      } catch {
        await authLogout();
        setCurrentUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    boot();
  }, []);

  const handleLoginSuccess = async (user: IUser) => {
    setCurrentUser(user);
    // Best-effort: refresh /me to populate roles/permissions fully.
    try {
      const me = await getMe();
      if (me.data?.user) setCurrentUser(me.data.user);
    } catch {}
  };

  // Load core data when logged in
  React.useEffect(() => {
    if (!currentUser) return;
    fetchCategories();
    fetchTemplates();
    fetchAssets();
    refreshCartCount();
  }, [currentUser, fetchCategories, fetchTemplates, fetchAssets, refreshCartCount]);

  React.useEffect(() => {
    if (!currentUser || deepLinkHandledRef.current) return;

    const assetId = deepLinkedAssetIdRef.current;
    if (!assetId) return;

    deepLinkHandledRef.current = true;
    setTab('browse');
    setSelectedAssetId(assetId);
  }, [currentUser]);

  // Debounced asset fetching for search/category changes
  React.useEffect(() => {
    if (!currentUser) return;
    const t = setTimeout(() => {
      fetchAssets();
    }, 250);
    return () => clearTimeout(t);
  }, [currentUser, searchTerm, selectedCategoryIds, fetchAssets]);

  const canAccessInhouse =
    hasPermission(currentUser, 'inhouse:read') ||
    hasPermission(currentUser, 'inhouse:create') ||
    hasPermission(currentUser, 'inhouse:update') ||
    hasPermission(currentUser, 'inhouse:delete');
  const canAccessListningar = hasAnyPermission(currentUser, [
    'listningar:read',
    'listningar:create',
    'listningar:update',
    'listningar:delete',
  ]);

  React.useEffect(() => {
    if (tab === 'inhouse' && !canAccessInhouse) {
      setTab('browse');
    }
  }, [tab, canAccessInhouse]);
  React.useEffect(() => {
    if (tab === 'listningar' && !canAccessListningar) {
      setTab('browse');
    }
  }, [tab, canAccessListningar]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Laddar…</Typography>
      </Box>
    );
  }

  if (!currentUser) {
    return <LoginPage onLoginSuccess={handleLoginSuccess as any} />;
  }

  const canDownload = hasPermission(currentUser, 'assets:download');
  const canUpload = hasPermission(currentUser, 'assets:create');

  const closeAssetDetails = () => setSelectedAssetId(null);

  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      <CssBaseline />
      <MaintenanceNotice topOffset={0} />

      {/* Main content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {tab === 'browse' && (
          <BrowseScreen
            categories={categories}
            selectedCategoryIds={selectedCategoryIds}
            onCategorySelect={setSelectedCategoryIds}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            columns={columns}
            onColumnsChange={setColumns}
            assets={assets}
            loadingAssets={loadingAssets || loadingCategories}
            assetError={assetError || categoryError}
            onSelectAsset={setSelectedAssetId}
            onAddToCart={addToCart}
          />
        )}
        {tab === 'upload' && (
          <UploadScreen
            categories={categories}
            loadingCategories={loadingCategories}
            onUploadSuccess={() => {
              // Refresh grid + keep cart badge fresh (optional)
              fetchAssets();
              showToast('Uppladdat', 'success');
              setTab('browse');
            }}
          />
        )}
        {tab === 'cart' && (
          <CartScreen
            onCartCountChange={(count) => {
              setCartCount(count);
            }}
          />
        )}
        {tab === 'listningar' && canAccessListningar && (
          <ListningarModule
            onClose={() => setTab('browse')}
            currentUser={currentUser}
          />
        )}
        {tab === 'inhouse' && canAccessInhouse && (
          <InhouseModule
            onClose={() => setTab('browse')}
            currentUser={currentUser}
          />
        )}
        {tab === 'more' && (
          <MoreScreen
            currentUser={currentUser}
            onLogout={handleLogout}
            onOpenTemplates={() => setTemplatesOpen(true)}
            onOpenSettings={(view) => {
              setSettingsView(view);
              setSettingsOpen(true);
            }}
          />
        )}
      </Box>

      {/* Bottom navigation */}
      <MobileFooterNav
        value={tab}
        onChange={(next) => {
          // If user lacks permission, never navigate into upload.
          if (next === 'upload' && !canUpload) {
            showToast('Du saknar behörighet att ladda upp', 'warning');
            return;
          }
          if (next === 'inhouse' && !canAccessInhouse) {
            showToast('Du saknar behörighet till Inhouse', 'warning');
            return;
          }
          setTab(next);
        }}
        cartCount={cartCount}
        canUpload={canUpload}
        canViewListningar={canAccessListningar}
        canViewInhouse={canAccessInhouse}
      />

      {/* Asset details (full-screen) */}
      {selectedAssetId && (
        <Dialog open fullScreen onClose={closeAssetDetails}>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ position: 'relative', height: '100%', bgcolor: '#f5f5f5' }}>
              {/* Swipe handle (optional close gesture) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9,
                }}
                onTouchStart={(e) => {
                  try {
                    detailSwipeStartYRef.current = e.touches?.[0]?.clientY ?? null;
                  } catch {
                    detailSwipeStartYRef.current = null;
                  }
                }}
                onTouchEnd={(e) => {
                  try {
                    const startY = detailSwipeStartYRef.current;
                    const endY = e.changedTouches?.[0]?.clientY ?? null;
                    detailSwipeStartYRef.current = null;
                    if (startY != null && endY != null && endY - startY > 90) {
                      closeAssetDetails();
                    }
                  } catch {
                    detailSwipeStartYRef.current = null;
                  }
                }}
              >
                <Box sx={{ width: 44, height: 5, borderRadius: 99, bgcolor: 'rgba(0,0,0,0.25)' }} />
              </Box>

              {/* Close button */}
              <IconButton
                onClick={closeAssetDetails}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'white' }}
                aria-label="Stäng"
              >
                <Box component="span" sx={{ fontSize: '24px', fontWeight: 'bold' }}>×</Box>
              </IconButton>

              <Box sx={{ pt: 3, px: 2, height: '100%', overflow: 'auto' }}>
                <AssetDetails
                  assetId={selectedAssetId}
                  allCategories={categories}
                  onAssetDeleted={() => {
                    closeAssetDetails();
                    fetchAssets();
                  }}
                  onAssetUpdate={fetchAssets}
                  collections={[]}
                  templates={templates}
                  // Mobile UX: keep admin actions out of the detail view (admin lives under "Mer").
                  isAdmin={false}
                  canUpdate={false}
                  canDelete={false}
                  canManageVersions={false}
                  canFavorite={false}
                  canDownload={canDownload}
                  canValidooSync={false}
                  canShare={false}
                  onClose={closeAssetDetails}
                  assetIds={assets.map((a) => a._id)}
                  onAssetChange={(newId) => setSelectedAssetId(newId)}
                />
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Settings full-screen (admin access via "Mer") */}
      {settingsOpen && (
        <Dialog open fullScreen onClose={() => setSettingsOpen(false)}>
          <DialogContent sx={{ p: 0 }}>
            <SettingsPage
              currentView={settingsView || 'roles'}
              currentUser={currentUser}
              onClose={() => setSettingsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Templates modal (admin access via "Mer") */}
      <TemplateManagementModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        categories={categories}
      />

      {/* Toasts */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

